import { useQuery } from '@tanstack/react-query';
import axios from 'axios';
import qs from 'qs';
import { useApiInfoContextValueHook } from '../contexts/ApiInfoContextProvider';
import { SellertoolSignatureUtils } from '../utils/sellertool-signature-utils';

const API_HOST = process.env.REACT_APP_MAIN_API_HOST;

/**
 * 워크스페이스의 product-options 목록을 react-query로 조회한다.
 * 서명 헤더(x-sellertool-*)는 기존 view 패턴과 동일하게 매 요청 timestamp로 생성한다.
 *
 * @param {object} args
 * @param {boolean} [args.enabled] - 조회 활성화 여부 (모달이 열렸을 때만 호출하도록 제어)
 * @param {string} [args.productName] - 상품명 검색어 (서버 필터)
 * @param {string} [args.ignoreSpaceYn] - 공백 무시 검색 여부 ('y' | 'n')
 */
export function useProductOptionsQueryHook({ enabled = true, productName, ignoreSpaceYn } = {}) {
    const { apiInfo } = useApiInfoContextValueHook();

    const query = useQuery({
        queryKey: ['product-options', apiInfo?.apiKey, productName ?? null, ignoreSpaceYn ?? null],
        queryFn: async () => {
            const timestamp = Date.now().toString();
            const signature = SellertoolSignatureUtils().generateSigniture({
                apiKey: apiInfo?.apiKey,
                secretKey: apiInfo?.secretKey,
                timestamp: timestamp,
            });

            const headers = {
                'x-sellertool-apiKey': apiInfo?.apiKey,
                'x-sellertool-timestamp': timestamp,
                'x-sellertool-signiture': signature,
            };

            const params = {};
            if (productName) {
                params.productName = productName;
            }
            if (ignoreSpaceYn) {
                params.ignoreSpaceYn = ignoreSpaceYn;
            }

            const res = await axios.get(`${API_HOST}/product-options`, {
                headers: headers,
                params: params,
                paramsSerializer: params => qs.stringify(params, { arrayFormat: 'repeat' }),
            });

            return res.data?.content ?? [];
        },
        enabled: enabled && !!apiInfo?.apiKey && !!apiInfo?.secretKey,
        staleTime: 1000 * 60 * 5,
    });

    return {
        productOptions: query.data ?? [],
        isLoading: query.isLoading,
        isFetching: query.isFetching,
        isError: query.isError,
        error: query.error,
        refetch: query.refetch,
    };
}
